import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Badge } from "../../../components/ui/badge";
import { Switch } from "../../../components/ui/switch";
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";

type BlacklistEntry = { value: string; isEnabled: boolean };
type BlacklistType = "ip";

interface BlacklistSectionProps {
	type: BlacklistType;
}

const blacklistConfig = {
	ip: {
		label: "IP Blacklist",
		placeholder: "e.g. 192.168.1.1",
		getAll: () => api.admin.blacklist.ip.all.get(),
		add: (value: string) => api.admin.blacklist.ip.add.post({ value }),
		toggle: (value: string, enable: boolean) => api.admin.blacklist.ip.toggle.patch({ value, enable }),
		del: (value: string) => api.admin.blacklist.ip.delete.delete({ value }),
	},
};

export function BlacklistSection({ type }: BlacklistSectionProps) {
	const { placeholder, getAll, add, toggle, del } = blacklistConfig[type];
	const [entries, setEntries] = useState<BlacklistEntry[]>([]);
	const [open, setOpen] = useState(false);
	const [value, setValue] = useState("");
	const [confirmValue, setConfirmValue] = useState<string | null>(null);
	const [isIPBlacklistEnabled, setIsIPBlacklistEnabled] = useState(true);
	const [moderationConfig, setModerationConfig] = useState<{
		isPatternBlacklistEnabled: boolean;
		isAIModerationEnabled: boolean;
		isSplitTextDetectionEnabled: boolean;
		splitWindowMessageCount: number;
		splitWindowSeconds: number;
		splitMaxMessageLength: number;
		aiBlockedMessageText: string;
	} | null>(null);
	const [saved, setSaved] = useState(false);

	async function load() {
		const { data } = await getAll();
		if (data) setEntries(data as BlacklistEntry[]);
	}

	useEffect(() => {
		load();
		api.admin.config.get().then(({ data }) => {
			if (!data) return;
			setIsIPBlacklistEnabled(data.isIPBlacklistEnabled);
			setModerationConfig({
				isPatternBlacklistEnabled: data.isPatternBlacklistEnabled,
				isAIModerationEnabled: data.isAIModerationEnabled,
				isSplitTextDetectionEnabled: data.isSplitTextDetectionEnabled,
				splitWindowMessageCount: data.splitWindowMessageCount,
				splitWindowSeconds: data.splitWindowSeconds,
				splitMaxMessageLength: data.splitMaxMessageLength,
				aiBlockedMessageText: data.aiBlockedMessageText,
			});
		});
	}, [type]);

	async function handleIPConfigChange(enable: boolean) {
		setIsIPBlacklistEnabled(enable);
		if (!moderationConfig) return;

		const { error } = await api.admin["update-blacklist-config"].patch({
			isIPBlacklistEnabled: enable,
			...moderationConfig,
		});
		if (!error) {
			setSaved(true);
			setTimeout(() => setSaved(false), 2000);
		}
	}

	async function handleAdd() {
		if (!value.trim()) return;
		await add(value.trim());
		setValue("");
		setOpen(false);
		load();
	}

	async function handleToggle(entry: BlacklistEntry) {
		await toggle(entry.value, !entry.isEnabled);
		load();
	}

	async function handleDelete(v: string) {
		await del(v);
		setConfirmValue(null);
		load();
	}

	return (
		<div className="space-y-4">
			<div className="flex max-w-sm items-center justify-between border border-[#111] p-3">
				<Label>IP Blacklist</Label>
				<div className="flex items-center gap-3">
					{saved && <span className="text-xs text-[#00ffcc66] font-mono">saved</span>}
					<Switch checked={isIPBlacklistEnabled} onCheckedChange={handleIPConfigChange} />
				</div>
			</div>

			<div className="flex items-center justify-between">
				<p className="text-xs text-[#333] font-mono">{entries.length} entries</p>
				<Button size="sm" onClick={() => setOpen(true)}>+ add entry</Button>
			</div>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Add Entry</DialogTitle>
					</DialogHeader>
					<div className="space-y-2">
						<Label>Value</Label>
						<Input
							value={value}
							onChange={(e) => setValue(e.target.value)}
							placeholder={placeholder}
							onKeyDown={(e) => e.key === "Enter" && handleAdd()}
						/>
					</div>
					<DialogFooter>
						<DialogClose asChild><Button variant="ghost" size="sm">cancel</Button></DialogClose>
						<Button size="sm" onClick={handleAdd}>add</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Table>
				<TableHeader>
					<TableRow>
						<TableHead>Value</TableHead>
						<TableHead>Status</TableHead>
						<TableHead>Enabled</TableHead>
						<TableHead className="w-20" />
					</TableRow>
				</TableHeader>
				<TableBody>
					{entries.map((entry) => (
						<TableRow key={entry.value}>
							<TableCell className="text-[#00ffcc]">{entry.value}</TableCell>
							<TableCell>
								<Badge variant={entry.isEnabled ? "destructive" : "muted"}>
									{entry.isEnabled ? "blocking" : "inactive"}
								</Badge>
							</TableCell>
							<TableCell>
								<Switch checked={entry.isEnabled} onCheckedChange={() => handleToggle(entry)} />
							</TableCell>
							<TableCell>
								{confirmValue === entry.value ? (
									<div className="flex gap-1">
										<Button size="sm" variant="destructive" onClick={() => handleDelete(entry.value)}>confirm</Button>
										<Button size="sm" variant="ghost" onClick={() => setConfirmValue(null)}>cancel</Button>
									</div>
								) : (
									<Button size="sm" variant="ghost" onClick={() => setConfirmValue(entry.value)}>delete</Button>
								)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
